export type ServicesIconKey = "tax" | "books" | "wealth" | "planning" | "owner" | "organize" | "team" | "app";
export type ServicesTemplateType = "generic" | "location" | "industry";
export type ServicesIndustrySlug = "any-all" | "film-production" | "creator";

export interface ServicesCard {
  title: string;
  description: string;
  icon: ServicesIconKey;
}

export interface ServicesPackage {
  name: string;
  description: string;
  priceDisplay: string;
  featured?: boolean;
  includes: string[];
  cta: string;
  target?: "signup" | "contact";
}

export interface ServicesReview {
  quote: string;
  name: string;
  role: string;
}

export interface ServicesOffice {
  slug: string;
  templateType: ServicesTemplateType;
  name: string;
  shortName: string;
  marketLabel: string;
  city: string;
  region: string;
  serviceArea: string;
  localDescriptor: string;
  servedAreas: string[];
  hours: string[];
  summary: string;
  heroTitle: string;
  heroDescription: string;
  heroSupportText: string;
  firmRoutingSlug?: string;
  industrySlug?: ServicesIndustrySlug;
  signupCtaLabel?: string;
  contactCtaLabel?: string;
  phoneDisplay?: string;
  phoneTel?: string;
  googleMapsUrl?: string;
  yelpUrl?: string;
  heroImageSrc?: string;
  heroImageAlt?: string;
  localSectionEyebrow?: string;
  localSectionTitle?: string;
  localSectionParagraphs?: string[];
  detailCardTitle?: string;
  detailCardDescription?: string;
  detailHighlights?: string[];
  showTeamProfiles?: boolean;
  showContactForm?: boolean;
  contactFormTitle?: string;
  contactFormDescription?: string;
  contactFormIntent?: string;
  reviews?: ServicesReview[];
  supportedEntityTypes: string[];
  services: ServicesCard[];
  process: ServicesCard[];
  packages: ServicesPackage[];
}

export const SERVICES_BRAND_NAME = "Fig Tax & Accounting";
export const SERVICES_DEFAULT_OFFICE_SLUG = "national";
export const SERVICES_PHONE_DISPLAY = "978-886-4072";
export const SERVICES_PHONE_TEL = "tel:+19788864072";
export const SERVICES_SUPPORTED_INDUSTRY_SLUGS: ServicesIndustrySlug[] = ["any-all", "film-production", "creator"];

const supportedEntityTypes = [
  "Freelancers and 1099 workers",
  "Single-member LLCs",
  "S-Corp owners",
  "Partnerships",
  "Multi-member LLCs",
  "Pass-through entities",
  "Local service businesses",
];

const services: ServicesCard[] = [
  {
    title: "Bookkeeping",
    description:
      "Monthly bookkeeping, transaction review, reconciliations, account cleanup, reporting, and tax-ready records.",
    icon: "books",
  },
  {
    title: "Tax Planning",
    description:
      "Quarterly tax estimates, deduction planning, entity-level coordination, retirement contribution planning, and year-end readiness.",
    icon: "planning",
  },
  {
    title: "Tax Preparation & Filing Support",
    description:
      "Tax preparation and e-filing support through qualified tax professionals, with clear review and approval workflows.",
    icon: "tax",
  },
  {
    title: "Wealth Management",
    description:
      "Investment advisory and financial planning services for clients who want business income, taxes, retirement, and long-term planning connected.",
    icon: "wealth",
  },
  {
    title: "Business Owner Planning",
    description:
      "Support for S-Corp owners, pass-through entities, cash flow planning, owner distributions, estimated taxes, and entity-level reporting.",
    icon: "owner",
  },
  {
    title: "Financial Organization",
    description:
      "A better system for keeping documents, transactions, open questions, and planning items out of scattered emails and spreadsheets.",
    icon: "organize",
  },
];

const process: ServicesCard[] = [
  {
    title: "Confirm fit and consent",
    description:
      "You start in the Fig Pro signup flow, review terms and data consent, and share contact details for the service relationship.",
    icon: "team",
  },
  {
    title: "Answer service-intake questions",
    description:
      "Fig collects the accounting, tax, retirement, payroll, contractor, sales tax, inventory, cleanup, deadline, and planning details needed to scope support.",
    icon: "app",
  },
  {
    title: "Accept the engagement letter",
    description:
      "Your engagement letter is generated from the selected service scope so you can review and accept it before billing begins.",
    icon: "organize",
  },
  {
    title: "Authorize payment",
    description:
      "Fixed monthly packages continue to Stripe Checkout for subscription billing. Custom-quote needs route to follow-up instead.",
    icon: "planning",
  },
  {
    title: "Open the Fig client app",
    description:
      "After payment, Fig preserves the lead link through auth and onboarding so the app can show your paid-service firm relationship.",
    icon: "tax",
  },
];

const packages: ServicesPackage[] = [
  {
    name: "Self Service",
    description:
      "For independent earners who want Fig app access and a connected financial picture without full-service firm support.",
    priceDisplay: "$24.99/mo",
    cta: "Start Self Service",
    includes: [
      "Fig app access",
      "Connected financial picture",
      "Tax estimate visibility",
      "Base retirement account options",
      "Can upgrade to full service",
    ],
  },
  {
    name: "Full Service Essentials",
    description: "Software plus accounting firm support for lower-complexity independent earners and solo owners.",
    priceDisplay: "$149.99/mo",
    featured: true,
    cta: "Start Essentials",
    includes: [
      "Everything in Self Service",
      "Bookkeeping support",
      "Tax estimate support",
      "Retirement-account coordination basics",
      "Best for solo or LLC clients without payroll, sales tax, inventory, rentals, multi-state activity, or cleanup complexity",
    ],
  },
  {
    name: "Full Service Plus",
    description:
      "Higher-complexity full-service support for owners with more involved bookkeeping, tax, payroll, or planning needs.",
    priceDisplay: "$249.99/mo",
    cta: "Start Plus",
    includes: [
      "Everything in Essentials",
      "S Corp and LLC taxed as S Corp complexity",
      "Payroll, contractor, and multi-state coordination",
      "Sales tax, inventory, rental property, or high transaction-volume support",
      "Prior-year cleanup and more involved tax planning",
    ],
  },
  {
    name: "Integrated Wealth",
    description:
      "Full Service Plus-style accounting coordination plus planning and advisory relationship support where appropriate.",
    priceDisplay: "Custom pricing",
    cta: "Get a Custom Quote",
    target: "contact",
    includes: [
      "Full Service Plus-style accounting coordination",
      "Payroll coordination",
      "Financial planning coordination",
      "Expanded retirement choices",
      "Investment advisory relationship, where appropriate",
    ],
  },
];

const sharedTemplate = {
  hours: ["Monday-Friday", "9:00 AM-5:00 PM Central"],
  supportedEntityTypes,
  services,
  process,
  packages,
};

const sharedLocationOffice = {
  ...sharedTemplate,
  templateType: "location" as const,
  phoneDisplay: SERVICES_PHONE_DISPLAY,
  phoneTel: SERVICES_PHONE_TEL,
  showTeamProfiles: true,
  signupCtaLabel: "Choose Your Support Level",
  contactCtaLabel: "Call the Team",
};

export const servicesOffices: ServicesOffice[] = [
  {
    ...sharedLocationOffice,
    templateType: "generic",
    slug: SERVICES_DEFAULT_OFFICE_SLUG,
    name: "Fig Tax & Accounting",
    shortName: "National",
    marketLabel: "Fig",
    city: "your city",
    region: "your region",
    serviceArea: "Serving independent earners, small business owners, and pass-through entity owners",
    localDescriptor: "independent earners and business owners",
    servedAreas: ["Bloomingdale", "Chicago", "Western Chicago suburbs"],
    summary:
      "Local financial help powered by software for independent earners, business owners, and pass-through entities.",
    heroTitle: "The service firm for people whose money does not fit in a W-2 box.",
    heroDescription:
      "Fig helps freelancers, business owners, and pass-through entities manage bookkeeping, taxes, and wealth planning from one connected financial picture.",
    heroSupportText:
      "Start online, get routed to an enabled Fig service team, and keep your books, tax estimates, documents, and financial plan organized throughout the year.",
  },
  {
    ...sharedLocationOffice,
    slug: "bloomingdale-il",
    firmRoutingSlug: "bloomingdale-il",
    name: "Bloomingdale, IL",
    shortName: "Bloomingdale",
    marketLabel: "Bloomingdale, IL",
    city: "Bloomingdale",
    region: "the western Chicago suburbs",
    serviceArea: "Serving Bloomingdale, Glendale Heights, Roselle, Carol Stream, Addison, and nearby suburbs",
    localDescriptor: "independent earners and business owners across the western Chicago suburbs",
    servedAreas: ["Bloomingdale", "Glendale Heights", "Roselle", "Carol Stream", "Addison"],
    summary: "A local Fig team for independent earners and business owners across the western Chicago suburbs.",
    heroTitle: "The local firm for people whose money does not fit in a W-2 box.",
    heroDescription:
      "Fig helps freelancers, business owners, and pass-through entities across the western Chicago suburbs manage bookkeeping, taxes, and wealth planning from one connected financial picture.",
    heroSupportText:
      "Work with a real Fig team in Bloomingdale, backed by software that keeps your books, tax estimates, documents, and financial plan organized throughout the year.",
  },
  {
    ...sharedLocationOffice,
    slug: "chicago-il",
    firmRoutingSlug: "chicago-il",
    name: "Chicago",
    shortName: "Chicago",
    marketLabel: "Chicago, IL",
    city: "Chicago",
    region: "Chicago",
    serviceArea: "Serving Chicago self-employed professionals, independent contractors, and pass-through businesses",
    localDescriptor: "independent earners and business owners across Chicago",
    servedAreas: ["Chicago", "West Loop", "River North", "Logan Square", "Lincoln Park"],
    summary: "A local Fig team for Chicago independent earners, small business owners, and pass-through entities.",
    heroTitle: "The local firm for people whose money does not fit in a W-2 box.",
    heroDescription:
      "Fig helps freelancers, business owners, and pass-through entities across Chicago manage bookkeeping, taxes, and wealth planning from one connected financial picture.",
    heroSupportText:
      "Work with a real Fig team in Chicago, backed by software that keeps your books, tax estimates, documents, and financial plan organized throughout the year.",
  },
];

export const servicesIndustryPages: ServicesOffice[] = [
  {
    ...sharedTemplate,
    slug: "film-production",
    templateType: "industry",
    industrySlug: "film-production",
    contactCtaLabel: "Contact Us",
    name: "Film Production",
    shortName: "Film Production",
    marketLabel: "Film production",
    city: "virtual",
    region: "film production",
    serviceArea: "Virtual service support for film crew, production workers, and project-based creative teams",
    localDescriptor: "film production professionals with project-based income",
    servedAreas: ["Film crew", "Production contractors", "Editors", "Cinematographers", "Creative studios"],
    summary: "Virtual accounting and tax support for film production professionals with project-based income.",
    heroTitle: "Accounting support built for film production income.",
    heroDescription:
      "Fig helps film crew, production contractors, and creative studios keep bookkeeping, taxes, retirement, and planning connected between shoots.",
    heroSupportText:
      "Start online and Fig Pro will route your service lead by industry to an enabled accounting firm that supports film production work.",
    heroImageSrc: "/assets/www/solutions/film-crew/hero.png",
    heroImageAlt: "Fig financial planning preview for film production professionals",
    localSectionEyebrow: "Virtual industry support",
    localSectionTitle: "Built for project-based production work, uneven pay cycles, and tax deadlines.",
    localSectionParagraphs: [
      "Film production workers often move between jobs, entities, locations, reimbursements, equipment expenses, and unpredictable income timing.",
      "Fig combines a structured service intake with accounting firm support so bookkeeping, estimates, retirement coordination, and planning decisions start from the same financial picture.",
    ],
    detailCardTitle: "Production-specific details we expect",
    detailCardDescription:
      "The signup flow asks the questions needed to route and scope your service relationship before engagement letter acceptance.",
    detailHighlights: [
      "1099, W-2, and entity income patterns",
      "Equipment, software, mileage, and production expenses",
      "Multi-state work and deadline pressure",
      "Retirement account and rollover coordination",
    ],
    reviews: [
      {
        quote:
          "Production income used to feel impossible to plan around. Fig helped me separate jobs, expenses, estimates, and retirement decisions without losing the thread between shoots.",
        name: "Maya P.",
        role: "Line Producer",
      },
      {
        quote:
          "My work moves across crews and states, and my books finally reflect that reality. The intake asked about the details my old tax workflow always missed.",
        name: "Andre L.",
        role: "Cinematographer",
      },
      {
        quote:
          "Equipment, software, contractor payments, and uneven project timing all show up in one place now. It makes tax planning feel less reactive.",
        name: "Jess K.",
        role: "Post-Production Studio Owner",
      },
    ],
    showContactForm: true,
    contactFormTitle: "Ask about film production services",
    contactFormDescription:
      "Send a note if you want help choosing the right service package before starting the signup flow.",
    contactFormIntent: "film-production-services",
  },
  {
    ...sharedTemplate,
    slug: "creator",
    templateType: "industry",
    industrySlug: "creator",
    contactCtaLabel: "Contact Us",
    name: "Creators",
    shortName: "Creators",
    marketLabel: "Creators",
    city: "virtual",
    region: "creator",
    serviceArea: "Virtual service support for creators, influencers, streamers, and independent media businesses",
    localDescriptor: "creators with platform, sponsorship, affiliate, and business income",
    servedAreas: ["Creators", "Influencers", "Streamers", "Podcasters", "Independent media businesses"],
    summary: "Virtual accounting and tax support for creators with platform, sponsorship, and business income.",
    heroTitle: "Accounting support for creators whose income comes from everywhere.",
    heroDescription:
      "Fig helps creators keep bookkeeping, estimated taxes, deductions, retirement, and planning organized across platform payouts, sponsorships, affiliate income, and business expenses.",
    heroSupportText:
      "Start online and Fig Pro will store your creator industry context before routing your service lead to an enabled accounting firm.",
    heroImageSrc: "/assets/www/solutions/influencers/hero.png",
    heroImageAlt: "Fig financial planning preview for creators",
    localSectionEyebrow: "Virtual industry support",
    localSectionTitle: "Built for creators with platform payouts, sponsor deals, deductions, and irregular income.",
    localSectionParagraphs: [
      "Creator finances can include multiple revenue streams, short-term campaigns, business expenses, estimated taxes, and retirement decisions that are easy to lose track of.",
      "Fig gives the service team a structured picture of your income, deadlines, entity type, and planning needs before the engagement starts.",
    ],
    detailCardTitle: "Creator-specific details we expect",
    detailCardDescription:
      "The signup flow captures the operating details that matter before fixed-price billing or custom-quote follow-up.",
    detailHighlights: [
      "Platform, sponsorship, affiliate, and product income",
      "Software, production, travel, and contractor expenses",
      "Entity, payroll, sales tax, and inventory complexity",
      "Integrated wealth and retirement planning interest",
    ],
    reviews: [
      {
        quote:
          "Sponsorships, platform payouts, and affiliate income all hit differently. Fig gave me a cleaner way to see what I earned, what I owed, and what I could set aside.",
        name: "Nina V.",
        role: "Creator",
      },
      {
        quote:
          "I needed support that understood launches, contractors, software, and irregular revenue. The signup questions felt built for the business I actually run.",
        name: "Caleb R.",
        role: "Streamer and Media Founder",
      },
      {
        quote:
          "My tax estimates and retirement planning finally connect back to my creator income instead of living in separate spreadsheets.",
        name: "Sofia M.",
        role: "Podcast Producer",
      },
    ],
    showContactForm: true,
    contactFormTitle: "Ask about creator services",
    contactFormDescription:
      "Send a note if you want help choosing the right service package before starting the signup flow.",
    contactFormIntent: "creator-services",
  },
];

export const servicesLocationOffices = servicesOffices.filter(
  (office) => office.slug !== SERVICES_DEFAULT_OFFICE_SLUG && office.templateType === "location",
);

export const servicesTemplates = [...servicesOffices, ...servicesIndustryPages];

export const getServicesOfficeBySlug = (slug?: string | null) => {
  return servicesOffices.find((office) => office.slug === (slug || SERVICES_DEFAULT_OFFICE_SLUG));
};

export const getServicesIndustryBySlug = (slug?: string | null) => {
  return servicesIndustryPages.find((industry) => industry.slug === slug);
};

export const getServicesTemplateBySlug = (slug?: string | null) => {
  return servicesTemplates.find((template) => template.slug === (slug || SERVICES_DEFAULT_OFFICE_SLUG));
};
